import React from 'react';
import * as Styled from './styled';
import { useLocale, INumberToStringFormatter, ITranslate } from 'locale';
import { isNil, lowerCase } from 'lodash';
import styled from '@emotion/styled';
import { SmallText } from 'ds/components/typography';
import { AvailabilityType } from 'utils/entities';
import Tooltip from 'ds/components/tooltip';
import QuestionIcon from 'assets/svg/question-border.svg';


export interface SecondaryInfoProps {
  currentPrice: number;
  generalCondition: string;
  monthlyTaxes: number;
  availableDate: number;
  availabilityType: string;
  furnitureDetails?: string;
  buildingYear: number;
  commonCharges?: number;
  area?: number;
  floors?: string | number;
  gardenAreas?: number[];
  warehouseAreas?: number[];
  balconyAreas?: number[];
  isFurnished?: boolean;
  buildingOnStilts?: boolean;
}

enum SecondaryInfoType {
  PricePerSquare = 'pricePerSquare',
  PropertyStatus = 'propertyStatus',
  TotalFloors = 'totalFloors',
  Furniture = 'furniture',
  Availability = 'availability',
  Tax = 'tax',
  Committee = 'committee',
  BuildingYear = 'buildingYear',
  GardenAreas = 'gardenAreas',
  WarehouseAreas = 'warehouseAreas',
  BalconyAreas = 'balconyAreas',
  IsFurnished = 'isFurnished',
  BuildingOnStilts = 'buildingOnStilts',
}

const infoTypesList: SecondaryInfoType[] = [
  SecondaryInfoType.PricePerSquare,
  SecondaryInfoType.PropertyStatus,
  SecondaryInfoType.Availability,
  SecondaryInfoType.TotalFloors,
  SecondaryInfoType.BuildingOnStilts,
  SecondaryInfoType.Furniture,
  SecondaryInfoType.Tax,
  SecondaryInfoType.Committee,
  SecondaryInfoType.BuildingYear,
  SecondaryInfoType.GardenAreas,
  SecondaryInfoType.BalconyAreas,
  SecondaryInfoType.WarehouseAreas,
  SecondaryInfoType.IsFurnished,
];

const arrayLikeInfoTypes = [
  SecondaryInfoType.GardenAreas,
  SecondaryInfoType.BalconyAreas,
  SecondaryInfoType.WarehouseAreas,
];

const simpleDateFormatter = (date: number) => {
  if (!date) return null;
  const d = new Date(date);
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${year}`;
};

function getInfoTypeValueByType(type: SecondaryInfoType, meta: SecondaryInfoProps, formatMoney: INumberToStringFormatter, t: ITranslate) {
  switch (type) {
    case SecondaryInfoType.PricePerSquare: {
      const { area, currentPrice } = meta;
      if (area) {
        return formatMoney(currentPrice / area);
      }
      return null;
    }
    case SecondaryInfoType.PropertyStatus:
      return meta.generalCondition ? t('unitPage.buildingDetails.value', { value: meta.generalCondition }) : null;
    case SecondaryInfoType.TotalFloors:
      if (!meta.floors) return null;
      return +meta.floors === 0 ? null : meta.floors;
    case SecondaryInfoType.Furniture:
      return meta.furnitureDetails ? t('unitPage.buildingDetails.value', { value: meta.furnitureDetails }) : null;
    case SecondaryInfoType.IsFurnished:
      return isNil(meta.isFurnished) ? null : t('unitPage.isFurnished', { value: meta.isFurnished });
    case SecondaryInfoType.Availability: {
      if (lowerCase(meta.availabilityType) === lowerCase(AvailabilityType.Date)) {
        return t('unitPage.buildingDetails.value', { value: simpleDateFormatter(meta.availableDate) });
      }
      return t('unitPage.buildingDetails.value', { value: meta.availabilityType });
    }
    case SecondaryInfoType.Tax:
      return meta.monthlyTaxes ? t('unitPage.buildingDetails.value', { value: formatMoney(meta.monthlyTaxes) }) : null;
    case SecondaryInfoType.Committee:
      return isNil(meta.commonCharges) ? null : formatMoney(meta.commonCharges);
    case SecondaryInfoType.BuildingYear:
      return meta.buildingYear;
    case SecondaryInfoType.BuildingOnStilts:
      return Boolean(meta.buildingOnStilts) ? t('unitPage.buildingOnStils.values', { value: meta.buildingOnStilts }) : null;
    default:
      return null;
  }
}

interface ListItemProps {
  title: string;
  value: string | number;
  locator: string;
  tooltipString?: string;
}

const Root = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  alignItems: 'center',
  backgroundColor: theme.colors.neutrals.grey11,
  borderRadius: theme.borderRadius.tiny,
}));

const Label = styled(SmallText)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.colors.neutrals.grey2,
  '.tooltip-wrapper + div': {
    display: 'none',
  },
  'svg': {
    cursor: 'pointer',
    display: 'block',
    'path': {
      fill: theme.colors.neutrals.grey4,
    },
  },
}));

const Value = styled(SmallText)(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

export const ListItem: React.FC<ListItemProps> = (props) => (
  <Root data-auto-secondary-item={props.locator} style={{ userSelect: 'none' }}>
    <Label>
      {props.title}
      {props.tooltipString ? (
        <Tooltip
          placement="top"
          usePowerTooltip
          tooltip={<SmallText>{props.tooltipString}</SmallText>}
        >
          <QuestionIcon />
        </Tooltip>
       ) : null}
    </Label>
    <Value weight="bold">{props.value}</Value>
  </Root>
);

export const SecondaryInfo: React.FC<SecondaryInfoProps> = (props) => {
  const { t, formatMoney } = useLocale();

  function createArrayLikeItem(type: SecondaryInfoType, values: number[]) {
    const amount = values.length;
    const idxToText = (index: number) => amount > 1 ? index + 1 : null;

    return values.map((value, index) => ({
      title: t('unitPage.secondary.info.ariasLabel', { label: type, id: idxToText(index) }),
      locator: type,
      value: value ? t('widget.prices.size', { size: value }) : '-',
    }));
  }

  const items = infoTypesList.map(type => {
    if (arrayLikeInfoTypes.includes(type)) {
      const values = props[type as keyof SecondaryInfoProps];
      if (Array.isArray(values)) {
        const isAreaWithoutSize = values.length === 1 && values[0] === 1;
        if (isAreaWithoutSize) {
          return null;
        }
        return createArrayLikeItem(type, values);
      }
    }

    return {
      title: t('unitPage.secondary.info.label', { label: type }),
      locator: type,
      value: getInfoTypeValueByType(type, props, formatMoney, t),
      tooltipString: t('unitPage.secondary.info.tooltipText', { type }),
    };
  }).flat().filter(Boolean);

  return (
    <Styled.Root>
      <Styled.Header data-auto="apartment-details-block" weight="bold">{t('unitPage.secondary.info.title')}</Styled.Header>
      <Styled.Body>
        {items.filter(({ value }) => !isNil(value)).map(meta => <ListItem key={meta.locator} {...meta} />)}
      </Styled.Body>
    </Styled.Root>
  );
};
